import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { 
  BsArrowLeft, BsClipboard, BsPerson, BsReceipt, BsExclamationTriangle, BsImage 
} from "react-icons/bs";
import { showCurrencySymbol, showTranslatedValue, formatTime12Hour } from "../../helper/generalHelper";
import api from "../../utils/api";
import ReceiptModal from "../../components/restaurant/Order/PrintBill";
import { useStatuses } from "../../hooks/useStatuses";
import { FaEdit } from "react-icons/fa";


const DetailItem = ({ label, icon, children, copyable = false }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (typeof children !== "string") return;
    navigator.clipboard.writeText(children);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm text-gray-600 flex items-center gap-1.5 shrink-0">
        {icon} {label}
      </span>
      <div className="flex items-center gap-1.5 min-w-0">
        <span
          className="text-sm font-medium text-gray-900 text-right truncate max-w-[160px]"
          title={typeof children === "string" ? children : undefined}
        >
          {children}
        </span>
        {copyable && typeof children === "string" && (
          <button
            onClick={handleCopy}
            className="shrink-0 text-gray-400 hover:text-gray-700"
            type="button"
          >
            {copied ? "✅" : "📋"}
          </button>
        )}
      </div>
    </div>
  );
};

const SingleOrder = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  const isStaff = window.location.pathname.includes("/restaurant-staff/");
  const apiPrefix = isStaff ? "restaurant-staff" : "restaurant";
  const navigatePrefix = isStaff ? "restaurant-staff/employee" : "restaurant";
  const [order, setOrder] = useState(location.state?.orderData || null);
  const [isLoading, setIsLoading] = useState(!location.state?.orderData);
  const [isPrintOpen, setIsPrintOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const { statuses, statusesLoading, findStatus } = useStatuses();
  const orderStatuses = statuses?.orders || [];

  const fetchOrders = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`${apiPrefix}/order/get?id=${id}`);
      setOrder(response.data || null);
    } catch (err) {
      console.error("Error fetching orders:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!order || order.id !== id) {
      fetchOrders();
    }
  }, [id]);

   const handleEditClick = (order) => {
    navigate(`/${navigatePrefix}/orders/edit/${order.id}`);
  };

  const handlePrintClick = (order) => {
    setSelectedOrder(order);
    setIsPrintOpen(true);
  };

  // Map statuses for progress bar
  const getProgressSteps = (currentStatusId) => {
    if (!orderStatuses.length) return [];

    const mainStatusIds = [2, 4, 5, 11];
    
    // Filter to only include the main 5 statuses
    const filteredStatuses = orderStatuses.filter((status) =>
      mainStatusIds.includes(Number(status.statusId))
    );

    // Sort to ensure correct sequence: Pending (2) -> Processing (4) -> Ready for Pickup (5) -> Completed (11)
    filteredStatuses.sort(
      (a, b) => mainStatusIds.indexOf(Number(a.statusId)) - mainStatusIds.indexOf(Number(b.statusId))
    );

    // Map currentStatusId to one of the main 5 status IDs for styling
    const currentStatusNum = Number(currentStatusId);
    let mappedCurrentId = currentStatusNum;

    if (currentStatusNum === 3) {
      mappedCurrentId = 4; // Confirmed -> Processing
    } else if ([6, 8, 9, 10].includes(currentStatusNum)) {
      mappedCurrentId = 11; // Delivered/Checked In/Checked Out -> Completed
    }

    const currentIndex = mainStatusIds.indexOf(mappedCurrentId);

    return filteredStatuses.map((status) => {
      const stepId = Number(status.statusId);
      const stepIndex = mainStatusIds.indexOf(stepId);
      
      let stepStatus = "pending";
      if (stepIndex === currentIndex) {
        stepStatus = "current";
      } else if (stepIndex < currentIndex) {
        stepStatus = "completed";
      }

      return {
        label: showTranslatedValue(status, "label"),
        status: stepStatus,
      };
    });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
        <p className="text-gray-500">Loading Order Details...</p>
      </div>
    );
  }

  if (!order && !isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh] px-4">
        <div className="p-8 rounded-2xl shadow-lg text-center w-full max-w-md border border-gray-200 bg-white">
          <BsExclamationTriangle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Order Not Found</h2>
          <p className="text-gray-600 mb-6">
            The order details couldn't be loaded. Please check the link or try again.
          </p>
          <button
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-3 text-white font-medium hover:bg-blue-700 transition-all duration-200 transform hover:-translate-y-0.5"
            onClick={() => navigate(-1)}
          >
            <BsArrowLeft className="h-4 w-4" /> Back to Orders
          </button>
        </div>
      </div>
    );
  }

  const progressSteps = getProgressSteps(order.order_status);

  const dateObj = new Date(order?.order_date || new Date());
  const shortDate = `${dateObj.getFullYear()}${String(dateObj.getMonth() + 1).padStart(2, '0')}${String(dateObj.getDate()).padStart(2, '0')}`;
  const shortId = order?.daily_order_number 
    ? `${shortDate}-${String(order.daily_order_number).padStart(3, '0')}`
    : `${shortDate}-${(order?.id || "").substring(0, 4).toUpperCase()}`;

  return (
    <div className="min-h-screen bg-gray-50 font-sans py-4 sm:py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-4 sm:mb-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div className="flex items-start sm:items-center gap-3 sm:gap-4 flex-col sm:flex-row w-full">
              <button
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors p-2 rounded-lg hover:bg-gray-100 shrink-0 self-start sm:self-center"
                onClick={() => navigate(-1)}
              >
                <BsArrowLeft className="h-5 w-5" />
                <span className="font-medium">Back</span>
              </button>
              <div className="hidden sm:block w-px h-6 bg-gray-300"></div>
              <div className="min-w-0 w-full">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight">
                  Order #<span className="text-primary break-all">{shortId}</span>
                </h1>
                <p className="text-gray-600 text-xs sm:text-sm mt-1">
                  Placed on {order.order_date} at {order.order_time}
                </p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <span className="text-xs px-2 py-1 rounded-full bg-yellow-100 text-yellow-800 capitalize">
                {order.order_status
                  ? showTranslatedValue(findStatus(order.order_status, "orders"), "label")
                  : "Unknown"}
              </span>
              <span
                className={`text-xs px-2 py-1 rounded-full ${
                  order.payment_status == "17"
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                } capitalize`}
              >
                {order.payment_status
                  ? showTranslatedValue(findStatus(order.payment_status, "payment"), "label")
                  : "Unknown"}
              </span>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-6">
            <div className="flex md:flex-row flex-col justify-between items-center gap-2 mb-3">
              {progressSteps.map((step, index) => (
                <div key={index} className="flex flex-col items-center flex-1">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                    step.status === "current" || step.status === "completed"
                      ? "bg-green-500 text-white" 
                      : "bg-gray-200 text-gray-500"
                  }`}>
                    {index + 1}
                  </div>
                  <span className={`text-xs mt-2 text-center ${
                    step.status === "current" || step.status === "completed" ? "text-green-600 font-medium" : "text-gray-500"
                  }`}>
                    {step.label}
                  </span>
                </div>
              ))}
            </div>

            <div className="relative">
              <div className="absolute top-3 left-0 right-0 h-0.5 bg-gray-200"></div>
              <div 
                className="absolute top-3 left-0 h-0.5 bg-green-500 transition-all duration-300"
                style={{ 
                  width: progressSteps.length > 1 
                    ? `${(progressSteps.filter(step => step.status === "current" || step.status === "completed").length - 1) / (progressSteps.length - 1) * 100}%`
                    : "0%"
                }}
              ></div>
            </div>
          </div>
        </div>

        <main className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Left Column - Order Items & Payment */}
          <div className="xl:col-span-2 space-y-6">
            {/* Order Items Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-200 flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <BsClipboard className="h-5 w-5 text-primary" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Order Items</h2>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                  {order?.order_details?.length} item{order.order_details?.length !== 1 ? 's' : ''}
                </span>
              </div>

              <div className="p-1">
                {order?.order_details?.map((item) => (
                  <div 
                    key={item.id} 
                    className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors rounded-lg m-2"
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <div className="relative">
                        {item.menu?.menu_images?.[0] ? (
                          <img 
                            src={item.menu.menu_images[0]} 
                            alt={item.menu.title || "Menu item"}
                            className="w-16 h-16 rounded-lg object-cover border border-gray-200"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div className={`w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center border border-gray-200 ${
                          item.menu?.menu_images?.[0] ? 'hidden' : 'flex'
                        }`}>
                          <BsImage className="h-6 w-6 text-blue-400" />
                        </div>
                        <div className="absolute -top-2 -right-2 bg-primary text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
                          {item.quantity}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate">
                          {showTranslatedValue(item.menu,'title') || "Unnamed Item"}
                        </h3>
                        <p className="text-gray-600 text-sm">{showCurrencySymbol(item.price)} each</p>
                        {item.menu?.short_description && (
                          <p className="text-gray-500 text-xs mt-1 truncate">{item.menu.short_description}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-gray-900 text-lg">{showCurrencySymbol(item.total_price)}</p>
                     <p className="text-gray-500 text-sm">{showCurrencySymbol(item.price)} × {item.quantity}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Payment Summary Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200 flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <BsReceipt className="h-5 w-5 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Payment Summary</h2>
              </div>
              
              <div className="p-6 space-y-4">
                <DetailItem label="Subtotal" >{showCurrencySymbol(order.subtotal)}</DetailItem>
                <DetailItem label="Tax" >{showCurrencySymbol(order.tax_amount)}</DetailItem>
                {parseFloat(order.extra_discount_amount) > 0 && (
                  <DetailItem label="Discount" icon="🎁">
                    <span className="text-green-600">- {showCurrencySymbol(order.extra_discount_amount)}</span>
                  </DetailItem>
                )}
                <div className="border-t border-gray-200 pt-4 flex justify-between items-center">
                  <span className="text-lg font-bold text-gray-900">Total Amount</span>
                  <span className="text-2xl font-bold text-primary">{showCurrencySymbol(order.grand_total)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Order Details */}
          <div className="space-y-6">
            {/* Customer & Order Details */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200 flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <BsPerson className="h-5 w-5 text-purple-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Order Details</h2>
              </div>
              <div className="p-6 space-y-4">
                <DetailItem label="Customer" icon="👤" copyable>
                  {order.customer_name?.trim() || "Guest Customer"}
                </DetailItem>

                {order.customer_mobile && (
                  <DetailItem label="Mobile" icon="📞" copyable>
                    {order.customer_mobile}
                  </DetailItem>
                )}

                {order.customer_email && (
                  <DetailItem label="Email" icon="📧" copyable>
                    {order.customer_email}
                  </DetailItem>
                )}

                <DetailItem label="Order Type" icon="📦">
                  <span className="capitalize">{order.order_type}</span>
                </DetailItem>

                {(order.table_number || order.table_name) && (
                  <DetailItem label="Table Name" icon="🪑">
                    {order.table_name || order.table_number || "Table"}
                  </DetailItem>
                )}

                <DetailItem label="Order Status" icon="📊">
                  <span className="text-xs px-2 py-1 rounded-full bg-yellow-100 text-yellow-800 capitalize">
                    {order.order_status ? showTranslatedValue(findStatus(order.order_status, "orders"), "label") : "Unknown"}
                  </span>
                </DetailItem>

                <DetailItem label="Payment Status" icon="💳">
                  <span className="text-xs px-2 py-1 rounded-full capitalize bg-gray-200 text-black">
                    {order.payment_status ? showTranslatedValue(findStatus(order.payment_status, "payment"), "label") : "Unknown"}
                  </span>
                </DetailItem>

                {(order.payment_status == 17 || order.payment_status == '17') && order.payment_mode && (
                  <DetailItem label="Payment Method" icon="💳">
                    <span className={`text-xs px-3 py-1 rounded-full uppercase font-bold border ${
                      order.payment_mode.toLowerCase() === 'cash' ? 'bg-green-100 text-green-700 border-green-200' :
                      order.payment_mode.toLowerCase() === 'upi' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                      order.payment_mode.toLowerCase() === 'card' ? 'bg-purple-100 text-purple-700 border-purple-200' :
                      'bg-gray-100 text-gray-700 border-gray-200'
                    }`}>
                      {order.payment_mode}
                    </span>
                  </DetailItem>
                )}

                <DetailItem label="Date & Time" icon="📅">
                  <div className="text-right">
                    <div>{order.order_date}</div>
                    <div className="text-sm text-gray-600">at {formatTime12Hour(order.order_time)}</div>
                  </div>
                </DetailItem>
              </div>
            </div>

            {/* Order Notes */}
            {order.note && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <BsClipboard className="h-4 w-4 text-gray-600" /> Order Notes
                  </h3>
                </div>
                <div className="p-6">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-gray-700 text-sm leading-relaxed break-words">{order.note}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
              <div className="p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
                <button
                  type="button"
                  className="w-full bg-primary mb-2 text-center text-white py-2.5 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                  onClick={() => handleEditClick(order)}
                >
                  Add Items
                </button>

                <button 
                  onClick={() => handlePrintClick(order)} 
                  className="w-full bg-primary text-white py-2.5 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  Print Receipt
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>

      {isPrintOpen && selectedOrder && (
        <ReceiptModal
          open={isPrintOpen}
          onClose={() => setIsPrintOpen(false)}
          orderData={selectedOrder}
        />
      )}
    </div>
  );
};

export default SingleOrder;